// src/components/reports/SupervisorReport/DetailedReport.tsx
import React, { useRef, useEffect } from 'react';
import {
    Box,
    Typography,
    TableContainer,
    Table,
    TableHead,
    TableBody,
    TableRow,
    Paper
} from '@mui/material';
import { SupervisorCustomerReportItem } from '../../../../types/sales';
import { StyledTableCell } from './styles';
import { SupervisorCustomerChart } from './SupervisorCustomerChart';

interface DetailedReportProps {
    data: SupervisorCustomerReportItem[];
    filteredData: SupervisorCustomerReportItem[];
    startMonth: string;
    selectedSupervisor: string;
    chartTitle?: string; // チャートのタイトル
}

export const DetailedReport: React.FC<DetailedReportProps> = ({
    data,
    filteredData,
    startMonth,
    selectedSupervisor,
    chartTitle
}) => {
    // レポート要素への参照を作成
    const reportRef = useRef<HTMLDivElement>(null);

    if (!data || filteredData.length === 0) return null;

    // 担当者名を取得（選択されている場合のみ）
    const supervisorTitle = selectedSupervisor || "全担当者";

    // 現在の年月を取得
    const currentYearMonth = `${startMonth.split('-')[0]}年${parseInt(startMonth.split('-')[1])}月`;

    return (
        <Box sx={{ width: '100%', mb: 6, position: 'relative' }}>
            {/* 印刷対象のレポート部分 - ref属性を追加してDOMアクセスを容易に */}
            <Box id="printable-report-section" ref={reportRef}>
                <Typography variant="h6" gutterBottom align="center" sx={{ mb: 1 }}>
                    月間売上・利益管理表
                </Typography>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6" sx={{
                        bgcolor: '#4CAF50',
                        color: 'white',
                        p: 1,
                        borderRadius: 1,
                        minWidth: '120px',
                        textAlign: 'center'
                    }}>
                        {currentYearMonth}
                    </Typography>

                    <Typography variant="h5" sx={{
                        fontWeight: 'bold',
                        textAlign: 'center',
                        flex: 1
                    }}>
                        {supervisorTitle}
                    </Typography>

                    <Box sx={{ minWidth: '120px' }}></Box>
                </Box>

                {/* レポートテーブル */}
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 4 }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <StyledTableCell className="gray-cell" sx={{ width: '25%' }}>会社名</StyledTableCell>
                                <StyledTableCell align="center" className="gray-cell" sx={{ width: '10%' }}>工事件数</StyledTableCell>
                                <StyledTableCell align="right" className="green-cell" sx={{ width: '15%' }}>売上金額</StyledTableCell>
                                <StyledTableCell align="right" className="green-cell" sx={{ width: '15%' }}>粗利</StyledTableCell>
                                <StyledTableCell align="right" className="pink-cell" sx={{ width: '15%' }}>マイナス</StyledTableCell>
                                <StyledTableCell align="right" className="pink-cell" sx={{ width: '15%' }}>最終・合計</StyledTableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredData.map((item, index) => (
                                <TableRow key={`${item.supervisor_name}-${index}`}
                                    sx={{
                                        bgcolor: index % 2 === 0 ? '#f0f8f0' : 'white',
                                    }}
                                >
                                    <StyledTableCell component="th" scope="row">{item.customer_name || '-'}</StyledTableCell>
                                    <StyledTableCell align="center">{item.project_count}件</StyledTableCell>
                                    <StyledTableCell align="right">
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                            <Box component="span" sx={{ mr: 1 }}>¥</Box>
                                            <Box component="span">{Number(item.total_sales).toLocaleString()}</Box>
                                        </Box>
                                    </StyledTableCell>
                                    <StyledTableCell align="right">
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                            <Box component="span" sx={{ mr: 1 }}>¥</Box>
                                            {/* <Box component="span">{Number(item.gross_profit).toLocaleString()}</Box> */}
                                            <Box component="span">{Number(item.final_profit).toLocaleString()}</Box>
                                        </Box>
                                    </StyledTableCell>
                                    <StyledTableCell align="right" sx={{ color: item.final_profit < 0 ? 'error.main' : undefined }}>
                                        {item.final_profit < 0 ? (
                                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                <Box component="span" sx={{ color: 'error.main', mr: 1 }}>¥</Box>
                                                <Box component="span" sx={{ color: 'error.main' }}>{Math.abs(Number(item.final_profit)).toLocaleString()}</Box>
                                            </Box>
                                        ) : '-'}
                                    </StyledTableCell>
                                    <StyledTableCell align="right">
                                        {item.final_profit >= 0 ? (
                                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                <Box component="span" sx={{ mr: 1 }}>¥</Box>
                                                <Box component="span">{Number(item.final_profit).toLocaleString()}</Box>
                                            </Box>
                                        ) : '-'}
                                    </StyledTableCell>
                                </TableRow>
                            ))}

                            {/* 合計行 */}
                            <TableRow>
                                <StyledTableCell className="total-row" align="center">
                                    合計
                                </StyledTableCell>
                                <StyledTableCell className="total-row" align="center">
                                    {filteredData.reduce((sum, item) => sum + Number(item.project_count), 0)}件
                                </StyledTableCell>
                                <StyledTableCell className="total-row" align="right">
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                        <Box component="span" sx={{ mr: 1 }}>¥</Box>
                                        <Box component="span" sx={{ color: 'primary.main' }}>
                                            {filteredData.reduce((sum, item) => sum + Number(item.total_sales), 0).toLocaleString()}
                                        </Box>
                                    </Box>
                                </StyledTableCell>
                                <StyledTableCell className="total-row" align="right">
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                        <Box component="span" sx={{ mr: 1 }}>¥</Box>
                                        <Box component="span">
                                            {filteredData.reduce((sum, item) => sum + Number(item.final_profit), 0).toLocaleString()}
                                        </Box>
                                    </Box>
                                </StyledTableCell>
                                <StyledTableCell className="total-row" align="right">
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                        <Box component="span" sx={{ mr: 1, color: 'error.main' }}>¥</Box>
                                        <Box component="span" sx={{ color: 'error.main' }}>
                                            {filteredData
                                                .filter(item => item.final_profit < 0)
                                                .reduce((sum, item) => sum + Math.abs(Number(item.final_profit)), 0)
                                                .toLocaleString()}
                                        </Box>
                                    </Box>
                                </StyledTableCell>
                                <StyledTableCell className="total-row" align="right">
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                        <Box component="span" sx={{ mr: 1 }}>¥</Box>
                                        <Box component="span" sx={{
                                            color: filteredData.reduce((sum, item) => sum + Number(item.final_profit), 0) < 0
                                                ? 'error.main'
                                                : 'success.main'
                                        }}>
                                            {filteredData.reduce((sum, item) => sum + Number(item.final_profit), 0).toLocaleString()}
                                        </Box>
                                    </Box>
                                </StyledTableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* 共通のSupervisorCustomerChartコンポーネントを使用 */}
                <Box sx={{ mt: 2 }}>
                    <SupervisorCustomerChart
                        data={data}
                        title={chartTitle || "売上・利益グラフ"}
                        fullSize={false}
                        selectedSupervisor={selectedSupervisor}
                    />
                </Box>
            </Box>
        </Box>
    );
};